/**
 * Source and the event types of SAM's own lifecycle events, as the API emits them
 * (apps/api/src/services/project-lifecycle-event-inputs.ts). Shared so filters built outside
 * the API, such as the web Automation page's watch presets, match what is emitted; an API
 * contract test (project-lifecycle-event-contract.test.ts) pins the two together.
 */
export const PROJECT_LIFECYCLE_EVENT_SOURCE = 'sam.lifecycle';
export const PROJECT_LIFECYCLE_EVENT_TYPES = {
    taskFailed: 'task.failed',
    sessionFailed: 'session.failed',
    releaseFailed: 'deployment.release.failed',
    releaseApplied: 'deployment.release.applied',
};
export const DEFAULT_PROJECT_EVENT_MAX_ACTIVE_SUBSCRIPTIONS_PER_PROJECT = 200;
export const DEFAULT_PROJECT_EVENT_FILTER_MAX_VALUES_PER_FIELD = 20;
export const DEFAULT_PROJECT_EVENT_FILTER_MAX_MATCH_KEYS = 100;
export const DEFAULT_PROJECT_EVENT_FILTER_MAX_STRING_BYTES = 160;
export const DEFAULT_PROJECT_EVENT_METADATA_MAX_BYTES = 8192;
export const DEFAULT_PROJECT_EVENT_METADATA_MAX_DEPTH = 4;
export const DEFAULT_PROJECT_EVENT_METADATA_MAX_KEYS = 64;
export const DEFAULT_PROJECT_EVENT_METADATA_MAX_ARRAY_ITEMS = 64;
export const DEFAULT_PROJECT_EVENT_DISPLAY_MAX_BYTES = 4096;
export const DEFAULT_PROJECT_EVENT_DISPLAY_MAX_LABELS = 12;
export const DEFAULT_PROJECT_EVENT_RAW_PAYLOAD_REF_MAX_BYTES = 512;
export const DEFAULT_PROJECT_EVENT_REASON_MAX_BYTES = 1024;
export const DEFAULT_PROJECT_EVENT_MAX_MATCHES_PER_EVENT = 100;
export const DEFAULT_PROJECT_EVENT_DELIVERY_BATCH_MAX_EVENTS = 50;
export const DEFAULT_PROJECT_EVENT_DELIVERY_ATTEMPT_MAX_PER_BATCH = 10;
export const DEFAULT_PROJECT_EVENT_LIST_LIMIT = 50;
export const DEFAULT_PROJECT_EVENT_LIST_MAX = 200;
export const DEFAULT_PROJECT_EVENT_SUBSCRIPTION_EVENT_CURSOR_MAX_LENGTH = 512;
export const DEFAULT_PROJECT_EVENT_RECENT_STATUS_LIMIT = 50;
export const DEFAULT_PROJECT_EVENT_RETENTION_DAYS = 30;
export const DEFAULT_PROJECT_EVENT_RETENTION_BATCH_ROWS = 500;
export const DEFAULT_PROJECT_EVENT_SOURCE_OUTBOX_BATCH_ROWS = 25;
export const DEFAULT_PROJECT_EVENT_SOURCE_OUTBOX_MAX_ATTEMPTS = 8;
export const DEFAULT_PROJECT_EVENT_SOURCE_OUTBOX_TTL_MS = 86_400_000;
export const DEFAULT_PROJECT_EVENT_SOURCE_OUTBOX_RETRY_BASE_MS = 30_000;
export const DEFAULT_PROJECT_EVENT_SOURCE_OUTBOX_RETRY_MAX_MS = 1_800_000;
export const DEFAULT_PROJECT_EVENT_SOURCE_OUTBOX_PROCESSING_LEASE_MS = 60_000;
export const DEFAULT_PROJECT_EVENT_RETENTION_INTERVAL_MS = 24 * 60 * 60 * 1000;
export const DEFAULT_PROJECT_EVENT_RETENTION_MIN_ALARM_DELAY_MS = 60 * 1000;
export const DEFAULT_PROJECT_EVENT_WAKE_MATERIALIZATION_MIN_ALARM_DELAY_MS = 1000;
export const DEFAULT_PROJECT_EVENT_WAKE_MATERIALIZATION_BACKOFF_BASE_MS = 5000;
export const DEFAULT_PROJECT_EVENT_WAKE_MATERIALIZATION_BACKOFF_MAX_MS = 5 * 60 * 1000;
export const DEFAULT_PROJECT_EVENT_WAKE_PROMPT_TTL_MS = 24 * 60 * 60 * 1000;
export const DEFAULT_PROJECT_EVENT_WAKE_READ_GRACE_MS = 24 * 60 * 60 * 1000;
export const DEFAULT_PROJECT_EVENT_WAKE_TARGET_COOLDOWN_MS = 30 * 1000;
export const DEFAULT_PROJECT_EVENT_WAKE_SUBSCRIPTION_COOLDOWN_MS = 30 * 1000;
export const DEFAULT_PROJECT_EVENT_WAKE_SUBSCRIPTION_LIFETIME_MS = 24 * 60 * 60 * 1000;
export const DEFAULT_PROJECT_EVENT_WAKE_MAX_PER_SUBSCRIPTION = 50;
export const DEFAULT_PROJECT_EVENT_SOURCE_OUTBOX_SWEEP_WALL_MS = 2_500;
export const DEFAULT_PROJECT_EVENT_SOURCE_OUTBOX_ADMISSION_TIMEOUT_MS = 5_000;
export const DEFAULT_PROJECT_EVENT_SOURCE_OUTBOX_TERMINAL_RETENTION_MS = 604_800_000;
export const DEFAULT_PROJECT_EVENT_LIMITS = {
    maxActiveSubscriptionsPerProject: DEFAULT_PROJECT_EVENT_MAX_ACTIVE_SUBSCRIPTIONS_PER_PROJECT,
    maxFilterValuesPerField: DEFAULT_PROJECT_EVENT_FILTER_MAX_VALUES_PER_FIELD,
    maxFilterMatchKeysPerSubscription: DEFAULT_PROJECT_EVENT_FILTER_MAX_MATCH_KEYS,
    maxFilterStringBytes: DEFAULT_PROJECT_EVENT_FILTER_MAX_STRING_BYTES,
    maxMetadataBytes: DEFAULT_PROJECT_EVENT_METADATA_MAX_BYTES,
    maxMetadataDepth: DEFAULT_PROJECT_EVENT_METADATA_MAX_DEPTH,
    maxMetadataKeys: DEFAULT_PROJECT_EVENT_METADATA_MAX_KEYS,
    maxMetadataArrayItems: DEFAULT_PROJECT_EVENT_METADATA_MAX_ARRAY_ITEMS,
    maxDisplayBytes: DEFAULT_PROJECT_EVENT_DISPLAY_MAX_BYTES,
    maxDisplayLabels: DEFAULT_PROJECT_EVENT_DISPLAY_MAX_LABELS,
    maxRawPayloadRefBytes: DEFAULT_PROJECT_EVENT_RAW_PAYLOAD_REF_MAX_BYTES,
    maxReasonBytes: DEFAULT_PROJECT_EVENT_REASON_MAX_BYTES,
    maxMatchesPerEvent: DEFAULT_PROJECT_EVENT_MAX_MATCHES_PER_EVENT,
    maxDeliveryBatchEvents: DEFAULT_PROJECT_EVENT_DELIVERY_BATCH_MAX_EVENTS,
    maxAttemptsPerBatch: DEFAULT_PROJECT_EVENT_DELIVERY_ATTEMPT_MAX_PER_BATCH,
    listLimitDefault: DEFAULT_PROJECT_EVENT_LIST_LIMIT,
    listLimitMax: DEFAULT_PROJECT_EVENT_LIST_MAX,
    subscriptionEventCursorMaxLength: DEFAULT_PROJECT_EVENT_SUBSCRIPTION_EVENT_CURSOR_MAX_LENGTH,
    recentStatusLimit: DEFAULT_PROJECT_EVENT_RECENT_STATUS_LIMIT,
    retentionDays: DEFAULT_PROJECT_EVENT_RETENTION_DAYS,
    retentionBatchRows: DEFAULT_PROJECT_EVENT_RETENTION_BATCH_ROWS,
    retentionIntervalMs: DEFAULT_PROJECT_EVENT_RETENTION_INTERVAL_MS,
    retentionMinAlarmDelayMs: DEFAULT_PROJECT_EVENT_RETENTION_MIN_ALARM_DELAY_MS,
    wakeMaterializationMinAlarmDelayMs: DEFAULT_PROJECT_EVENT_WAKE_MATERIALIZATION_MIN_ALARM_DELAY_MS,
    wakeMaterializationBackoffBaseMs: DEFAULT_PROJECT_EVENT_WAKE_MATERIALIZATION_BACKOFF_BASE_MS,
    wakeMaterializationBackoffMaxMs: DEFAULT_PROJECT_EVENT_WAKE_MATERIALIZATION_BACKOFF_MAX_MS,
    wakePromptTtlMs: DEFAULT_PROJECT_EVENT_WAKE_PROMPT_TTL_MS,
    wakeReadGraceMs: DEFAULT_PROJECT_EVENT_WAKE_READ_GRACE_MS,
    wakeTargetCooldownMs: DEFAULT_PROJECT_EVENT_WAKE_TARGET_COOLDOWN_MS,
    wakeSubscriptionCooldownMs: DEFAULT_PROJECT_EVENT_WAKE_SUBSCRIPTION_COOLDOWN_MS,
    wakeSubscriptionLifetimeMs: DEFAULT_PROJECT_EVENT_WAKE_SUBSCRIPTION_LIFETIME_MS,
    wakeMaxPerSubscription: DEFAULT_PROJECT_EVENT_WAKE_MAX_PER_SUBSCRIPTION,
};
//# sourceMappingURL=project-events.js.map